<template>
  <div class="p-6">
    <!-- Page Header -->
    <div class="flex justify-between items-center mb-8 border-b pb-4">
      <h1 class="text-3xl font-bold text-gray-800">My Tests</h1>
      <button @click="createTestPrompt" class="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg shadow hover:bg-blue-700 transition">
        <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        New Test
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-10">
      <p class="text-gray-500">Loading tests...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="tests.length === 0" class="text-center bg-gray-50 p-12 rounded-lg">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">No tests found</h3>
      <p class="mt-1 text-sm text-gray-500">Get started by creating your first test.</p>
      <div class="mt-6">
        <button @click="createTestPrompt" class="flex items-center mx-auto bg-blue-600 text-white px-4 py-2 rounded-lg shadow hover:bg-blue-700 transition">
          <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Create New Test
        </button>
      </div>
    </div>

    <!-- Test List (Card Layout) -->
    <div v-else class="space-y-4">
      <div
          v-for="test in tests"
          :key="test.id"
          class="bg-white p-4 rounded-lg shadow-md flex justify-between items-center transition hover:shadow-lg hover:border-blue-500 border border-transparent"
      >
        <div class="flex items-center">
          <div class="p-3 bg-blue-100 rounded-lg mr-4">
            <svg class="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <span class="text-lg font-medium text-gray-800">{{ test.name }}</span>
        </div>
        <div class="flex items-center space-x-2">
          <button @click="goToTestDetail(test.id)" class="text-white bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded-md transition">
            Open
          </button>
          <button @click="deleteTest(test.id)" class="text-gray-500 hover:text-red-600 p-2 rounded-full transition">
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from '../services/axiosInstance'
import { useModalStore } from '@/stores/modal'

const tests = ref([])
const loading = ref(true)
const router = useRouter()
const modal = useModalStore()

const fetchTests = async () => {
  loading.value = true
  try {
    const response = await axios.get('/tests')
    tests.value = response.data
  } catch (error) {
    console.error('Error fetching tests:', error)
  } finally {
    loading.value = false
  }
}

const createTestPrompt = async () => {
  const testName = await modal.prompt(
    'Enter the name for your new test:',
    'Create New Test',
    'e.g., Chapter 5 Biology Quiz',
    'Create'
  )

  if (!testName) return

  try {
    const response = await axios.post('/tests/create', { name: testName })
    const newTest = response.data
    router.push({
      name: 'QuestionsUpload',
      params: { testId: newTest.id },
      query: { isNew: 'true' }
    })
  } catch (error) {
    console.error('Error creating test:', error)
    modal.error('Failed to create test. Please try again.')
  }
}

const deleteTest = async (testId) => {
  const confirmed = await modal.confirm(
    'Are you sure you want to delete this test? This action cannot be undone.',
    'Delete Test',
    'Delete',
    'Cancel'
  )

  if (confirmed) {
    try {
      await axios.delete(`/tests/${testId}`)
      tests.value = tests.value.filter(test => test.id !== testId)
      modal.success('Test deleted successfully.')
    } catch (error) {
      console.error('Error deleting test:', error)
      modal.error('Could not delete test.')
    }
  }
}

const goToTestDetail = (testId) => {
  router.push(`/tests/${testId}`)
}

onMounted(fetchTests)
</script>
